import React, { useState } from 'react';

// Define types for our component
interface XmlToHtmlProps {}

// Parse XML and generate HTML
const parseXmlToHtml = (xml: string): string => {
  try {
    // Create a DOM parser and parse the XML string
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Get the root element
    const rootElement = xmlDoc.documentElement;
    
    // Function to generate HTML from an XML node
    const generateHtml = (node: Element, indent: string = ''): string => {
      let html = '';
      const tagName = node.tagName;
      const hasChildren = node.children.length > 0;
      const hasText = node.textContent && node.textContent.trim() !== '';
      
      // Start tag
      html += `${indent}<div class="xml-element">\n`;
      html += `${indent}  <div class="xml-tag-name">&lt;${tagName}`;
      
      // Add attributes
      if (node.attributes.length > 0) {
        Array.from(node.attributes).forEach(attr => {
          html += ` <span class="xml-attribute">${attr.name}="${attr.value}"</span>`;
        });
      }
      
      // Close tag if no children
      if (!hasChildren && !hasText) {
        html += '/&gt;</div>\n';
      } else {
        html += '&gt;</div>\n';
        
        // If node has text content but no children
        if (!hasChildren && hasText) {
          html += `${indent}  <div class="xml-content">${node.textContent}</div>\n`;
        }
        
        // Process children
        if (hasChildren) {
          html += `${indent}  <div class="xml-children">\n`;
          Array.from(node.children).forEach(child => {
            html += generateHtml(child as Element, indent + '    ');
          });
          html += `${indent}  </div>\n`;
        }
        
        // End tag
        html += `${indent}  <div class="xml-tag-name">&lt;/${tagName}&gt;</div>\n`;
      }
      
      html += `${indent}</div>\n`;
      return html;
    };
    
    // Generate the HTML
    const htmlOutput = generateHtml(rootElement);
    
    // Wrap with styling
    return `
<!DOCTYPE html>
<html>
<head>
  <style>
    .xml-element {
      margin-left: 20px;
      border-left: 1px solid #ccc;
      padding-left: 10px;
      margin-bottom: 5px;
    }
    .xml-tag-name {
      color: #0000cc;
      font-weight: bold;
      font-family: monospace;
    }
    .xml-attribute {
      color: #009900;
      font-family: monospace;
    }
    .xml-content {
      margin-left: 20px;
      font-family: monospace;
    }
    .xml-children {
      margin-left: 20px;
    }
    .collapsible {
      cursor: pointer;
    }
    .collapsed > .xml-children {
      display: none;
    }
  </style>
  <script>
    function toggleCollapse(element) {
      element.classList.toggle('collapsed');
    }
  </script>
</head>
<body>
  <div class="xml-viewer">
    ${htmlOutput}
  </div>
  <script>
    // Add click handlers for collapsible elements
    document.addEventListener('DOMContentLoaded', function() {
      const elements = document.querySelectorAll('.xml-element');
      elements.forEach(element => {
        const tagName = element.querySelector('.xml-tag-name');
        if (tagName && element.querySelector('.xml-children')) {
          tagName.classList.add('collapsible');
          tagName.addEventListener('click', function() {
            toggleCollapse(element);
          });
        }
      });
    });
  </script>
</body>
</html>`;
  } catch (error) {
    throw error;
  }
};

// XML to HTML transform with indentation and syntax highlighting
const formatXmlAsHtml = (xml: string, indentSize: number = 2): string => {
  try {
    // Parse the XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "application/xml");
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("XML Parsing Error: " + parseError[0].textContent);
    }
    
    // Function to generate HTML with syntax highlighting
    const generateSyntaxHighlightedHtml = (node: Node, indent: string = ''): string => {
      let html = '';
      
      // Handle text nodes
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue?.trim();
        if (text) {
          html += `${indent}<span class="xml-text">${text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>\n`;
        }
        return html;
      }
      
      // Handle element nodes
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        
        // Opening tag
        html += `${indent}<span class="xml-bracket">&lt;</span><span class="xml-tag">${element.nodeName}</span>`;
        
        // Attributes
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          html += ` <span class="xml-attr-name">${attr.nodeName}</span><span class="xml-attr-equals">=</span><span class="xml-attr-value">"${attr.nodeValue?.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}"</span>`;
        }
        
        // Check if self-closing or has children
        if (element.childNodes.length === 0) {
          html += `<span class="xml-bracket">/&gt;</span>\n`;
        } else {
          html += `<span class="xml-bracket">&gt;</span>\n`;
          
          // Process children
          for (let i = 0; i < element.childNodes.length; i++) {
            html += generateSyntaxHighlightedHtml(element.childNodes[i], indent + ' '.repeat(indentSize));
          }
          
          // Closing tag
          html += `${indent}<span class="xml-bracket">&lt;/</span><span class="xml-tag">${element.nodeName}</span><span class="xml-bracket">&gt;</span>\n`;
        }
        
        return html;
      }
      
      // Handle other node types (comments, processing instructions, etc.)
      if (node.nodeType === Node.COMMENT_NODE) {
        const commentText = node.nodeValue || '';
        html += `${indent}<span class="xml-comment">&lt;!-- ${commentText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')} --&gt;</span>\n`;
        return html;
      }
      
      if (node.nodeType === Node.PROCESSING_INSTRUCTION_NODE) {
        const piNode = node as ProcessingInstruction;
        html += `${indent}<span class="xml-pi">&lt;?${piNode.target} ${piNode.data}?&gt;</span>\n`;
        return html;
      }
      
      return html;
    };
    
    // Generate the HTML
    let formattedHtml = '';
    
    // Add XML declaration if present
    const declaration = xmlDoc.firstChild;
    if (declaration && declaration.nodeType === Node.PROCESSING_INSTRUCTION_NODE) {
      const piNode = declaration as ProcessingInstruction;
      formattedHtml += `<span class="xml-pi">&lt;?${piNode.target} ${piNode.data}?&gt;</span>\n`;
    }
    
    // Process the document element
    formattedHtml += generateSyntaxHighlightedHtml(xmlDoc.documentElement);
    
    // Wrap with styles
    return `
<!DOCTYPE html>
<html>
<head>
  <style>
    body {
      font-family: monospace;
      white-space: pre-wrap;
      background-color: #f8f8f8;
      padding: 20px;
    }
    .xml-bracket { color: #0000cc; }
    .xml-tag { color: #0000cc; font-weight: bold; }
    .xml-attr-name { color: #9c5d27; }
    .xml-attr-equals { color: #000000; }
    .xml-attr-value { color: #009900; }
    .xml-text { color: #000000; }
    .xml-comment { color: #808080; font-style: italic; }
    .xml-pi { color: #8a2be2; }
    .line-numbers {
      color: #888;
      margin-right: 10px;
      user-select: none;
      text-align: right;
      display: inline-block;
      width: 30px;
    }
  </style>
</head>
<body>
  <div class="xml-formatted">
    ${formattedHtml}
  </div>
</body>
</html>`;
  } catch (error) {
    throw error;
  }
};

// Generate XML document from HTML template
const generateXmlFromTemplate = (template: string): string => {
  try {
    // Parse template parameters
    const rootElement = template === 'custom' ? 'root' : template;
    
    // Create a simple XML structure based on the template
    let xmlDoc = '';
    
    switch (template) {
      case 'book-catalog':
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<catalog>
  <book id="bk101">
    <author>Gambardella, Matthew</author>
    <title>XML Developer's Guide</title>
    <genre>Computer</genre>
    <price>44.95</price>
    <publish_date>2000-10-01</publish_date>
    <description>An in-depth look at creating applications with XML.</description>
  </book>
  <book id="bk102">
    <author>Ralls, Kim</author>
    <title>Midnight Rain</title>
    <genre>Fantasy</genre>
    <price>5.95</price>
    <publish_date>2000-12-16</publish_date>
    <description>A former architect battles corporate zombies.</description>
  </book>
</catalog>`;
        break;
        
      case 'rss-feed':
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0">
  <channel>
    <title>Sample RSS Feed</title>
    <link>https://example.com</link>
    <description>A sample RSS feed for demonstration</description>
    <language>en-us</language>
    <pubDate>Tue, 10 Jun 2023 04:00:00 GMT</pubDate>
    <item>
      <title>First Article</title>
      <link>https://example.com/article1</link>
      <description>This is the description of the first article</description>
      <pubDate>Mon, 09 Jun 2023 16:20:00 GMT</pubDate>
    </item>
    <item>
      <title>Second Article</title>
      <link>https://example.com/article2</link>
      <description>This is the description of the second article</description>
      <pubDate>Mon, 09 Jun 2023 14:15:00 GMT</pubDate>
    </item>
  </channel>
</rss>`;
        break;
        
      case 'website-config':
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<website>
  <settings>
    <title>My Website</title>
    <description>A personal website</description>
    <base_url>https://mywebsite.com</base_url>
    <theme>dark</theme>
    <language>en</language>
  </settings>
  <navigation>
    <item>
      <name>Home</name>
      <url>/</url>
      <order>1</order>
    </item>
    <item>
      <name>About</name>
      <url>/about</url>
      <order>2</order>
    </item>
    <item>
      <name>Contact</name>
      <url>/contact</url>
      <order>3</order>
    </item>
  </navigation>
  <footer>
    <copyright>© 2023 My Website</copyright>
    <social>
      <platform name="twitter">https://twitter.com/myhandle</platform>
      <platform name="github">https://github.com/myusername</platform>
    </social>
  </footer>
</website>`;
        break;
        
      case 'address-book':
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<address-book>
  <contact id="c1">
    <name>John Doe</name>
    <email>john.doe@example.com</email>
    <phone type="home">555-1234</phone>
    <phone type="mobile">555-5678</phone>
    <address>
      <street>123 Main St</street>
      <city>Anytown</city>
      <state>CA</state>
      <zip>12345</zip>
    </address>
  </contact>
  <contact id="c2">
    <name>Jane Smith</name>
    <email>jane.smith@example.com</email>
    <phone type="home">555-8765</phone>
    <phone type="mobile">555-4321</phone>
    <address>
      <street>456 Oak Ave</street>
      <city>Somewhere</city>
      <state>NY</state>
      <zip>67890</zip>
    </address>
  </contact>
</address-book>`;
        break;
        
      default:
        xmlDoc = `<?xml version="1.0" encoding="UTF-8"?>
<${rootElement}>
  <!-- Add your XML content here -->
  <element attribute="value">
    <child>Text content</child>
    <child>More content</child>
  </element>
  <another-element>
    <nested>
      <deeply>Very nested content</deeply>
    </nested>
  </another-element>
</${rootElement}>`;
    }
    
    return xmlDoc;
  } catch (error) {
    throw error;
  }
};

// Main component to convert XML to HTML
const XmlToHtml: React.FC<XmlToHtmlProps> = () => {
  // State for XML input
  const [xmlInput, setXmlInput] = useState<string>('');
  // State for HTML output
  const [htmlOutput, setHtmlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for file name
  const [fileName, setFileName] = useState<string>('xml_converted');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State to track if conversion happened
  const [isHtmlGenerated, setIsHtmlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for conversion mode
  const [conversionMode, setConversionMode] = useState<string>('interactive');
  // State for showing preview
  const [showPreview, setShowPreview] = useState<boolean>(false);
  // State for indent size
  const [indentSize, setIndentSize] = useState<number>(2);
  // State for template selection
  const [template, setTemplate] = useState<string>('custom');

  // Handle XML input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setXmlInput(e.target.value);
    setError('');
    setIsHtmlGenerated(false);
    setShowPreview(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use template
  const useTemplate = () => {
    try {
      const xmlTemplate = generateXmlFromTemplate(template);
      setXmlInput(xmlTemplate);
      setError('');
      setIsHtmlGenerated(false);
      setShowPreview(false);
    } catch (e) {
      setError(`Error generating template: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Format/Beautify XML
  const formatXml = () => {
    try {
      if (!xmlInput.trim()) {
        setError('Please enter XML data to format');
        return;
      }
      
      // Parse the XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlInput, "application/xml");
      
      // Check for parsing errors
      const parseError = xmlDoc.getElementsByTagName("parsererror");
      if (parseError.length > 0) {
        throw new Error("XML Parsing Error: " + parseError[0].textContent);
      }
      
      // Serialize back to string with pretty formatting
      const serializer = new XMLSerializer();
      let formattedXml = '';
      
      // Function to pretty print XML with indentation
      const prettyPrintXml = (node: Node, indent: string = ''): string => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.nodeValue?.trim();
          return text ? indent + text + '\n' : '';
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          let result = indent + '<' + element.nodeName;
          
          // Add attributes
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            result += ` ${attr.nodeName}="${attr.nodeValue}"`;
          }
          
          // Check if there are any child nodes
          if (element.childNodes.length === 0) {
            result += '/>\n';
            return result;
          }
          
          result += '>\n';
          
          // Process child nodes
          for (let i = 0; i < element.childNodes.length; i++) {
            result += prettyPrintXml(element.childNodes[i], indent + '  ');
          }
          
          result += indent + '</' + element.nodeName + '>\n';
          return result;
        }
        
        return '';
      };
      
      // Add XML declaration
      formattedXml += '<?xml version="1.0" encoding="UTF-8"?>\n';
      
      // Format the document element
      formattedXml += prettyPrintXml(xmlDoc.documentElement);
      
      setXmlInput(formattedXml);
      setError('');
    } catch (e) {
      setError(`Error formatting XML: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert XML to HTML
  const convertXmlToHtml = () => {
    setError('');
    setIsLoading(true);
    setIsHtmlGenerated(false);
    setHtmlOutput('');
    setShowPreview(false);
    
    if (!xmlInput.trim()) {
      setError('Please enter XML data');
      setIsLoading(false);
      return;
    }

    try {
      let result = '';
      
      // Choose conversion method based on mode
      if (conversionMode === 'interactive') {
        result = parseXmlToHtml(xmlInput);
      } else {
        result = formatXmlAsHtml(xmlInput, indentSize);
      }
      
      setHtmlOutput(result);
      setIsHtmlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting XML to HTML: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setXmlInput('');
    setHtmlOutput('');
    setError('');
    setFileName('xml_converted');
    setIsHtmlGenerated(false);
    setShowPreview(false);
  };

  // Copy HTML to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(htmlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download HTML file
  const downloadHtml = () => {
    const blob = new Blob([htmlOutput], { type: 'text/html;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'xml_converted'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Toggle preview
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">XML to HTML Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert XML data to HTML. Choose between an interactive tree view or syntax-highlighted HTML for displaying your XML content.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="xmlInput" className="font-medium text-gray-700">
                XML Input
              </label>
              <div className="flex space-x-2">
                <select
                  className="text-sm border border-gray-300 rounded p-1"
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                >
                  <option value="custom">Custom</option>
                  <option value="book-catalog">Book Catalog</option>
                  <option value="rss-feed">RSS Feed</option>
                  <option value="website-config">Website Config</option>
                  <option value="address-book">Address Book</option>
                </select>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useTemplate}
                >
                  Use Template
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={formatXml}
                >
                  Format XML
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setXmlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="xmlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={xmlInput}
              onChange={handleInputChange}
              placeholder="Paste your XML data here, e.g.,
<?xml version='1.0' encoding='UTF-8'?>
<library>
  <book id='1'>
    <title>Book Title</title>
    <author>Author Name</author>
    <year>2023</year>
  </book>
  <book id='2'>
    <title>Another Book</title>
    <author>Another Author</author>
    <year>2024</year>
  </book>
</library>"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                  File Name
                </label>
                <input
                  type="text"
                  id="fileName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={fileName}
                  onChange={handleFileNameChange}
                  placeholder="Enter file name (without extension)"
                />
              </div>
              
              <div>
                <label htmlFor="conversionMode" className="block text-sm font-medium text-gray-700 mb-1">
                  Conversion Mode
                </label>
                <select
                  id="conversionMode"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={conversionMode}
                  onChange={(e) => setConversionMode(e.target.value)}
                >
                  <option value="interactive">Interactive Tree View</option>
                  <option value="highlighted">Syntax Highlighted</option>
                </select>
              </div>
            </div>
            
            {conversionMode === 'highlighted' && (
              <div className="mb-4">
                <label htmlFor="indentSize" className="block text-sm font-medium text-gray-700 mb-1">
                  Indent Size
                </label>
                <select
                  id="indentSize"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={indentSize}
                  onChange={(e) => setIndentSize(parseInt(e.target.value))}
                >
                  <option value="2">2 spaces</option>
                  <option value="4">4 spaces</option>
                  <option value="8">8 spaces</option>
                </select>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertXmlToHtml}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to HTML'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              HTML Output
            </label>
            
            {isHtmlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy HTML'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadHtml}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={togglePreview}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isHtmlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    HTML output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isHtmlGenerated && !showPreview && (
              <div className="flex-grow flex flex-col">
                <textarea
                  id="htmlOutput"
                  className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={htmlOutput}
                  readOnly
                />
              </div>
            )}
            
            {!error && isHtmlGenerated && showPreview && (
              <div className="flex-grow border border-gray-300 rounded-md">
                <iframe
                  srcDoc={htmlOutput}
                  title="HTML Preview"
                  className="w-full h-full rounded-md"
                  sandbox="allow-scripts"
                />
              </div>
            )}
          </div>
          
          {isHtmlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">XML Successfully Converted to HTML!</p>
              </div>
              <p className="text-sm">
                You can now copy, download, or preview the generated HTML.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About XML to HTML Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms XML documents into HTML format using two different approaches. The Interactive Tree View creates a collapsible, nested representation of your XML that's easy to navigate. The Syntax Highlighted option produces a code-style view with color-coded elements, attributes, and values.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Interactive Tree View</strong> - Collapsible elements for easy navigation of complex XML</li>
              <li><strong>Syntax Highlighting</strong> - Color-coded XML elements, attributes, and values</li>
              <li><strong>Customizable Indentation</strong> - Adjust spacing for better readability</li>
              <li><strong>Template Selection</strong> - Start with common XML structures</li>
              <li><strong>Live Preview</strong> - View the converted HTML in real-time</li>
              <li><strong>Error Handling</strong> - Clear error messages for invalid XML input</li>
              <li><strong>One-Click Download</strong> - Save the converted HTML with a single click</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Interactive Tree View Mode</h3>
            <p className="text-gray-600">
              The Interactive Tree View mode transforms your XML into a navigable structure with the following features:
            </p>
            <ul className="list-disc pl-5 text-gray-600 mt-2">
              <li>Collapsible elements for easy exploration of nested data</li>
              <li>Color-coded distinction between tags, attributes, and content</li>
              <li>Visually organized hierarchy with indentation</li>
              <li>Click on any parent element to expand or collapse its children</li>
              <li>Clear visual separation between different levels of the XML tree</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Syntax Highlighted Mode</h3>
            <p className="text-gray-600">
              The Syntax Highlighted mode formats your XML as code with enhanced readability:
            </p>
            <ul className="list-disc pl-5 text-gray-600 mt-2">
              <li>Color-coded syntax highlighting for tags, attributes, and values</li>
              <li>Properly indented structure based on your selection (2, 4, or 8 spaces)</li>
              <li>Preservation of XML comments and processing instructions</li>
              <li>Monospaced font for code-like presentation</li>
              <li>Ideal for documentation, tutorials, or code examples</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format XML" button to check if your XML is valid before conversion</li>
              <li>For large XML files, the Syntax Highlighted mode may be more efficient</li>
              <li>The Interactive Tree View works best for exploring complex XML structures</li>
              <li>Start with a template to understand the converter's capabilities</li>
              <li>Use the preview feature to see how your HTML will appear in a browser</li>
              <li>For documentation purposes, include both the original XML and the converted HTML</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Use Cases</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Documentation</strong> - Create readable documentation of XML APIs or configurations</li>
              <li><strong>Data Exploration</strong> - Visually navigate and understand complex XML structures</li>
              <li><strong>Web Presentation</strong> - Display XML data on websites in a more user-friendly format</li>
              <li><strong>Teaching</strong> - Create educational materials about XML structure and syntax</li>
              <li><strong>Debugging</strong> - Visualize XML responses from APIs or system outputs</li>
              <li><strong>Reporting</strong> - Include formatted XML data in technical reports or presentations</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlToHtml;